<script setup>
import axios from 'axios'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/vue/20/solid'
import { computed, ref, reactive, onMounted } from 'vue'
import dayjs from 'dayjs'
import updateLocale from 'dayjs/plugin/updateLocale'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isToday from 'dayjs/plugin/isToday'
import Swal from 'sweetalert2'
import { useVuelidate } from '@vuelidate/core'
import { workRules } from '@/helpers/work_validator'

dayjs.extend(updateLocale)
dayjs.extend(isSameOrBefore)
dayjs.extend(isToday)
dayjs.updateLocale('en', {
  weekStart: 1,
  weekdaysShort: ['日', '月', '火', '水', '木', '金', '土']
})

const props = defineProps({
  creatorId: {
    type: String,
    required: true
  },
  currentUser: {
    type: Object,
    required: true
  },
  workPeriod: {
    type: Object,
    required: true
  },
  shiftOptions: {
    type: Array,
    required: true
  },
  shiftTimeTypeId: {
    type: Number,
    required: true
  }
})
const needsEmailNotification = ref(false)
const data = reactive({
  fetched: {},
  displayWorks: [],
  isLoading: false,
  isEmptyWorkPeriod: false
})
const creatorData = reactive({
  isExpired: false,
  isLocked: false,
  ignoreExpiration: false,
  shiftTimeType: props.shiftOptions.find(
    (shiftTimeType) => shiftTimeType.id === props.shiftTimeTypeId
  )
})
const dateData = reactive({
  indexOfCalendar: {},
  startOfCalendar: {},
  endOfCalendar: {},
  displayedStartOfCalendar: {},
  displayedEndOfCalendar: {},
  displayDates: []
})
const noteData = reactive({
  editOfferNote: null,
  editEntryNote: null
})
const setData = () => {
  data.isEmptyWorkPeriod = props.workPeriod['first_date'] === undefined
  creatorData.isExpired = dayjs(props.workPeriod['expired_at']) < dayjs()
  data.registrablePeriod = `（${dayjs(props.workPeriod['first_date']).format(
    'YYYY/MM/DD'
  )} - ${dayjs(props.workPeriod['last_date']).format('YYYY/MM/DD')}）`
  dateData.startOfCalendar = dayjs(props.workPeriod['first_date']).startOf(
    'week'
  )
  dateData.endOfCalendar = dayjs(props.workPeriod['last_date'])
    .endOf('week')
    .startOf('day')
  setInitialDisplayPeriod()
}
const v$ = useVuelidate(workRules, data)

const fetchWorks = async () => {
  let result = await axios.get(
    `/creators/${
      props.creatorId
    }/works.json?start_date=${dateData.startOfCalendar.format('YYYY-MM-DD')}\
      &end_date=${dateData.endOfCalendar.format('YYYY-MM-DD')}`
  )
  data.name = result.data.name
  data.fetched = result.data
  creatorData.ignoreExpiration = result.data['ignore_expiration']
  creatorData.isLocked =
    creatorData.isExpired &&
    props.currentUser['is_admin_or_scheduler'] === false &&
    !creatorData.ignoreExpiration
}
const buildTargetWorks = () => {
  const result = []
  let indexDay = dateData.startOfCalendar.clone()
  while (indexDay.isSameOrBefore(dateData.endOfCalendar)) {
    if (
      data.fetched.works.find(
        (work) => work.date === indexDay.format('YYYY-MM-DD')
      )
    )
      result.push(
        data.fetched.works.find(
          (work) => work.date === indexDay.format('YYYY-MM-DD')
        )
      )
    else {
      result.push({
        date: indexDay.format('YYYY-MM-DD'),
        status: 'adjusting',
        offer_id: null,
        offer_note: '',
        entry_id: null,
        entry_note: ''
      })
    }

    indexDay = indexDay.add(1, 'day')
  }
  return result
}
const buildDisplayWorks = () => {
  data.displayWorks = []
  data.targetWorks.forEach((work) => {
    if (
      dayjs(work.date) >= dateData.displayedStartOfCalendar &&
      dayjs(work.date) <= dateData.displayedEndOfCalendar
    )
      data.displayWorks.push(work)
  })
}

const dayBgColor = (day) => {
  if (isRegistrationInvalidDay(day)) {
    switch (day.status) {
      case 'adjusting':
        return 'bg-[#e4e2d0]'
      case 'monthly_fixed':
        return 'bg-[#d7dde5]'
      case 'weekly_fixed':
        return 'bg-[#d8e3db]'
      default:
        return 'bg-gray-50 text-gray-500'
    }
  }

  switch (day.status) {
    case 'adjusting':
      return 'bg-yellow-50'
    case 'monthly_fixed':
      return 'bg-blue-50'
    case 'weekly_fixed':
      return 'bg-green-50'
    default:
      return 'bg-gray-50 text-gray-500'
  }
}

const toggleEditOffer = (targetDay) => {
  if (targetDay) {
    noteData.editOfferNote = targetDay
  } else {
    noteData.editOfferNote = null
  }
}

const toggleEditEntry = (targetDay) => {
  if (targetDay) {
    noteData.editEntryNote = targetDay
  } else {
    noteData.editEntryNote = null
  }
}

const setDefaultShiftTimeType = () => {
  data.targetWorks.forEach((work) => {
    if (isRegistrationInvalidDay(work.date)) return
    if (work.status !== 'adjusting') return
    work.entry_id = creatorData.shiftTimeType.id
  })
}

onMounted(async () => {
  data.isLoading = true
  await setData()
  await fetchWorks()
  data.targetWorks = buildTargetWorks()
  await buildDisplayWorks()
  data.isLoading = false
})

const isDisabledPreviousButton = () =>
  dayjs(data.displayWorks[0]?.date) <= dateData.startOfCalendar
const isDisabledNextButton = () =>
  dayjs(data.displayWorks.slice(-1)[0]?.date) >= dateData.endOfCalendar
const isRegistrationInvalidDay = (date) => {
  return (
    creatorData.isLocked ||
    dayjs(date).isBefore(dayjs(), 'day') ||
    dayjs(date).isBefore(dayjs(props.workPeriod['first_date']), 'day') ||
    dayjs(dayjs(props.workPeriod['last_date'])).isBefore(dayjs(date), 'day')
  )
}
const setPreviousDisplayWorks = () => {
  dateData.indexOfCalendar = dateData.indexOfCalendar.subtract(1, 'M')
  dateData.displayedStartOfCalendar = dateData.indexOfCalendar.startOf('week')
  dateData.displayedEndOfCalendar = dateData.indexOfCalendar
    .endOf('month')
    .endOf('week')
  buildDisplayWorks()
}
const setNextDisplayWorks = () => {
  dateData.indexOfCalendar = dateData.indexOfCalendar.add(1, 'M')
  dateData.displayedStartOfCalendar = dateData.indexOfCalendar.startOf('week')
  dateData.displayedEndOfCalendar = dateData.indexOfCalendar
    .endOf('month')
    .endOf('week')
  buildDisplayWorks()
}
const setInitialDisplayPeriod = () => {
  dateData.indexOfCalendar = dayjs(props.workPeriod['first_date']).startOf(
    'month'
  )
  dateData.displayedStartOfCalendar = dayjs(
    props.workPeriod['first_date']
  ).startOf('week')
  dateData.displayedEndOfCalendar = dateData.indexOfCalendar
    .clone()
    .endOf('month')
    .endOf('week')
}

const setFirstDisplayWorks = () => {
  setInitialDisplayPeriod()
  buildDisplayWorks()
}

const payload = computed(() => {
  return {
    work_registration: {
      needs_email_notification:
        props.workPeriod['published_at'] !== null &&
        needsEmailNotification.value,
      works: data.targetWorks.filter(
        (work) =>
          dayjs(work.date) >= dayjs(props.workPeriod['first_date']) &&
          dayjs(work.date) <= dayjs(props.workPeriod['last_date'])
      )
    }
  }
})

const submit = async () => {
  const modal = await Swal.fire({
    title: '登録してもよろしいですか？',
    text: '',
    confirmButtonText: 'はい',
    confirmButtonColor: '#40BF6C',
    cancelButtonText: 'キャンセル',
    showCancelButton: true,
    reverseButtons: true,
    focusCancel: true
  })
  if (modal.isConfirmed) {
    data.isLoading = true
    try {
      await axios.post(
        `/creators/${props.creatorId}/work_registrations`,
        payload.value
      )
      data.isLoading = false
      await Swal.fire({
        icon: 'success',
        title: '登録が完了しました',
        confirmButtonText: '閲覧画面へ移動',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
      window.location.href = `/creators/${props.creatorId}/schedules`
    } catch (error) {
      data.isLoading = false
      await Swal.fire({
        icon: 'error',
        title: '登録に失敗しました',
        text: '入力内容をご確認ください',
        confirmButtonText: 'OK',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
    }
  }
}

const labButtonPadding = props.currentUser.is_creator_or_agent
  ? '0.3rem'
  : '1rem'
const creatorButtonPadding = props.currentUser.is_creator_or_agent
  ? '1rem'
  : '0.3rem'
</script>

<template>
  <vue-element-loading :active="data.isLoading" is-full-screen />
  <div class="relative pb-10 pt-8">
    <div class="pb-6">
      <div class="mx-auto px-4 sm:px-6 md:px-8">
        <div
          v-if="data.isEmptyWorkPeriod"
          class="lg:flex lg:h-full lg:flex-col"
        >
          <header class="px-1 py-3">
            <div class="mb-8 flex items-center justify-between">
              <div class="text-2xl font-semibold text-gray-800">
                月間スケジュール登録
              </div>
            </div>
          </header>
          <div class="mt-10 grid grid-cols-12">
            <div class="col-span-8 col-start-3">
              <div class="overflow-hidden rounded-lg bg-white px-6 py-5 shadow">
                <div
                  class="flex justify-center font-medium leading-6 text-gray-700"
                >
                  現在受付中の月間スケジュールはございません
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="lg:flex lg:h-full lg:flex-col">
          <div
            v-if="creatorData.isExpired"
            class="mb-10 border-l-4 border-yellow-400 bg-yellow-50 p-4"
          >
            <div class="flex">
              <div class="flex-shrink-0">
                <svg
                  class="h-6 w-6 text-yellow-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
              <div class="ml-3">
                <div
                  v-if="creatorData.ignoreExpiration"
                  class="text-yellow-700"
                >
                  提出期限（{{
                    dayjs(props.workPeriod['expired_at']).format(
                      'YYYY年MM月DD日HH:mm'
                    )
                  }}）を過ぎていますが、一時的にロックが解除されています。
                </div>
                <div v-else class="text-yellow-700">
                  提出期限（{{
                    dayjs(props.workPeriod['expired_at']).format(
                      'YYYY年MM月DD日HH:mm'
                    )
                  }}）を過ぎているため、ロックされています。
                </div>
              </div>
            </div>
          </div>

          <header class="px-1 py-3">
            <div class="lg:flex lg:items-center lg:justify-between">
              <div class="min-w-0 flex-1">
                <div class="text-2xl font-semibold text-gray-800">
                  月間スケジュール登録
                  <span class="text-xl text-gray-700">{{
                    data.registrablePeriod
                  }}</span>
                </div>
                <div
                  class="mb-8 ml-3 mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-8"
                >
                  <div class="mt-2 flex items-center text-sm text-gray-500">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      class="mr-1 h-4 w-4"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                      />
                    </svg>
                    {{ data.name }}
                  </div>
                  <div class="mt-2 flex flex-row items-center text-sm">
                    <div class="mr-1 flex flex-row items-center text-gray-500">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="mr-1 h-4 w-4"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      提出期限:
                    </div>
                    <div class="font-semibold">
                      {{
                        dayjs(props.workPeriod['expired_at']).format(
                          'YYYY/M/D HH:mm'
                        )
                      }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="mt-5 flex lg:ml-4 lg:mt-0">
                <div class="flex flex-col">
                  <div class="flex flex-row items-center">
                    <div
                      v-if="
                        props.currentUser.is_admin_or_scheduler === true &&
                        props.workPeriod['published_at'] !== null
                      "
                    >
                      <input
                        id="email-notification"
                        v-model="needsEmailNotification"
                        type="checkbox"
                        class="text-indigo-600 focus:ring-indigo-500 mr-2 h-4 w-4 rounded border-gray-300"
                        :disabled="creatorData.isLocked"
                      />
                      <label
                        for="email-notification"
                        class="select-none font-medium text-gray-700"
                      >
                        更新をメールで通知する
                      </label>
                    </div>
                    <button
                      :class="[
                        v$.$invalid || creatorData.isLocked
                          ? 'disabled:opacity-25'
                          : 'hover:bg-yellow-600',
                        'ml-6 rounded-md border border-transparent bg-yellow-500 px-10 py-2 font-medium text-white shadow-sm  focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:ring-offset-2'
                      ]"
                      :disabled="v$.$invalid || creatorData.isLocked"
                      @click="submit"
                    >
                      日程登録
                    </button>
                  </div>
                  <div
                    v-if="v$.$invalid"
                    class="mt-2 flex flex-row-reverse text-sm font-semibold text-red-600"
                  >
                    <div class="flex">
                      <ExclamationCircleIcon
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                      />
                      <div>コメントが長すぎる日があります</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex items-center justify-between">
              <div
                class="w-full sm:flex md:ml-3 md:inline-block md:items-center lg:flex lg:items-center"
              >
                <div class="flex items-center">
                  <div
                    class="mr-2 flex h-8 items-center rounded-md shadow-sm md:items-stretch"
                  >
                    <button
                      class="flex items-center justify-center rounded-l-md border border-r-0 border-gray-300 bg-white py-2 pl-3 pr-4 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:px-2 md:hover:bg-gray-50"
                      :disabled="isDisabledPreviousButton()"
                      @click="setPreviousDisplayWorks"
                    >
                      <span class="sr-only">Previous month</span>
                      <ChevronLeftIcon class="h-5 w-5" aria-hidden="true" />
                    </button>
                    <button
                      class="hidden whitespace-nowrap border-b border-t border-gray-300 bg-white px-3.5 text-xs font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-800 focus:relative md:block"
                      @click="setFirstDisplayWorks"
                    >
                      初月
                    </button>
                    <span
                      class="relative -mx-px h-5 w-px bg-gray-300 md:hidden"
                    />
                    <button
                      :disabled="isDisabledNextButton()"
                      class="flex items-center justify-center rounded-r-md border border-l-0 border-gray-300 bg-white py-2 pl-4 pr-3 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:px-2 md:hover:bg-gray-50"
                      @click="setNextDisplayWorks"
                    >
                      <span class="sr-only">Next month</span>
                      <ChevronRightIcon class="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div
                  class="hidden lg:ml-4 lg:flex lg:h-6 lg:w-px lg:bg-gray-300"
                />
                <div
                  class="flex flex-wrap items-center whitespace-nowrap text-xs md:flex md:items-center"
                >
                  <div class="py-3">
                    <div
                      class="ml-4 mr-2 inline-block h-3 w-3 rounded-full bg-orange-300"
                    ></div>
                    <span class="text-gray-700">Lab.提案</span>
                  </div>
                  <div class="py-3">
                    <div
                      class="ml-5 mr-2 inline-block h-3 w-3 rounded-full bg-red-300"
                    ></div>
                    <span class="text-gray-700">クリエイター提出</span>
                  </div>
                  <div class="py-3">
                    <div
                      class="ml-5 mr-2 inline-block h-3 w-3 rounded-full bg-blue-300"
                    ></div>
                    <span class="text-gray-700">月間確定</span>
                  </div>
                  <div
                    class="ml-5 h-8 w-16 border border-gray-300 bg-yellow-50 opacity-80"
                  >
                    <span class="flex justify-center leading-8 text-gray-800"
                      >調整中</span
                    >
                  </div>
                  <div
                    class="ml-2 h-8 w-16 border border-gray-300 bg-blue-50 opacity-80"
                  >
                    <span class="flex justify-center leading-8 text-gray-800"
                      >月確</span
                    >
                  </div>
                  <div
                    class="ml-2 h-8 w-16 border border-gray-300 bg-green-50 opacity-80"
                  >
                    <span class="flex justify-center leading-8 text-gray-800"
                      >週確</span
                    >
                  </div>
                </div>
                <div v-if="creatorData.shiftTimeType" class="ml-auto">
                  <button
                    class="ml-2 rounded-md border border-transparent bg-gray-400 px-2 py-1.5 text-xs text-white shadow-sm hover:bg-gray-500 focus:outline-none"
                    @click="setDefaultShiftTimeType"
                  >
                    全て[{{ creatorData.shiftTimeType.display_name }}]をセット
                  </button>
                </div>
              </div>
            </div>
          </header>
          <div
            class="shadow ring-1 ring-gray-300 ring-opacity-80 lg:flex lg:flex-auto lg:flex-col"
          >
            <div
              class="sr-only grid grid-cols-7 gap-px border-b border-gray-300 border-opacity-80 bg-gray-300 bg-opacity-80 text-center text-xs font-semibold leading-6 text-gray-700 lg:not-sr-only lg:flex-none"
            >
              <div class="bg-white py-2">月</div>
              <div class="bg-white py-2">火</div>
              <div class="bg-white py-2">水</div>
              <div class="bg-white py-2">木</div>
              <div class="bg-white py-2">金</div>
              <div class="bg-white py-2 text-blue-400">土</div>
              <div class="bg-white py-2 text-red-400">日</div>
            </div>
            <!-- カレンダー内側 -->
            <div
              class="flex bg-gray-200 text-xs leading-6 text-gray-700 lg:flex-auto"
            >
              <div class="w-full lg:grid lg:grid-cols-7 lg:gap-px">
                <div
                  v-for="(day, index) in data.displayWorks"
                  :key="day.date"
                  :class="[dayBgColor(day), 'relative flex px-3 py-2 lg:block']"
                >
                  <div
                    class="flex flex-col pr-3 lg:flex-row lg:justify-between lg:pr-0"
                  >
                    <time
                      :datetime="day.date"
                      :class="
                        dayjs(day.date).isToday()
                          ? 'flex h-6 w-6 items-center justify-center rounded-full bg-yellow-500 p-4 font-bold text-white'
                          : 'font-bold'
                      "
                    >
                      {{ dayjs(day.date).format('M/D') }}
                    </time>
                    <div
                      class="not-sr-only lg:sr-only"
                      :class="{
                        'text-blue-400':
                          dayjs(day.date).format('ddd') === 'Sat',
                        'text-red-400': dayjs(day.date).format('ddd') === 'Sun'
                      }"
                    >
                      {{ dayjs(day.date).format('ddd') }}
                    </div>
                  </div>

                  <!-- カレンダー1日分-->
                  <div class="w-full">
                    <!--  Labメモ -->
                    <template v-if="noteData.editOfferNote === day.date">
                      <div class="flex items-center justify-between">
                        <textarea
                          v-model="day.offer_note"
                          rows="4"
                          class="focus:border-indigo-500 focus:ring-indigo-500 block w-full rounded-md border-gray-300 p-1 shadow-sm sm:text-sm"
                        />
                        <button
                          v-if="
                            !isRegistrationInvalidDay(day.date) &&
                            props.currentUser.is_admin_or_scheduler === true
                          "
                          class="bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 inline-flex items-center rounded-full border border-transparent p-1 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
                          @click.prevent="toggleEditOffer"
                        >
                          <CheckCircleIcon class="h-5 w-5" aria-hidden="true" />
                        </button>
                      </div>
                    </template>
                    <template v-else>
                      <div class="flex items-center justify-between">
                        <div class="editable-text">
                          {{ day.offer_note }}
                        </div>
                        <button
                          v-if="
                            !isRegistrationInvalidDay(day.date) &&
                            props.currentUser.is_admin_or_scheduler === true
                          "
                          class="bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 inline-flex items-center rounded-full border border-transparent p-1 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
                          @click.prevent="toggleEditOffer(day.date)"
                        >
                          <PencilSquareIcon
                            class="h-5 w-5"
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                    </template>
                    <div
                      v-if="
                        v$.targetWorks.$each.$response.$errors[index]
                          .offer_note[0]
                      "
                      class="mt-2 flex text-sm font-semibold text-red-600"
                    >
                      <ExclamationCircleIcon
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                      />
                      <div>
                        {{
                          v$.targetWorks.$each.$response.$errors[index]
                            .offer_note[0].$message
                        }}
                      </div>
                    </div>
                    <!--スケジューラーボタン-->
                    <div class="mb-4 mt-2">
                      <SelectButton
                        v-model="day.offer_id"
                        :options="shiftOptions"
                        option-label="display_name"
                        option-value="id"
                        aria-labelledby="single"
                        class="p-button-lab"
                        :disabled="
                          isRegistrationInvalidDay(day.date) ||
                          props.currentUser['is_admin_or_scheduler'] === false
                        "
                      />
                    </div>
                    <!-- クリエイターボタン-->
                    <div class="mb-2 mt-4">
                      <SelectButton
                        v-model="day.entry_id"
                        :options="shiftOptions"
                        option-label="display_name"
                        option-value="id"
                        aria-labelledby="single"
                        class="p-button-creator"
                        :disabled="
                          isRegistrationInvalidDay(day.date) ||
                          day.status !== 'adjusting' ||
                          (props.currentUser['is_admin_or_scheduler'] ===
                            false &&
                            props.currentUser['is_creator_or_agent'] === false)
                        "
                      />
                    </div>
                    <!-- クリエイターメモ -->
                    <template v-if="noteData.editEntryNote === day.date">
                      <div class="flex items-center justify-between">
                        <textarea
                          v-model="day.entry_note"
                          rows="4"
                          class="focus:border-indigo-500 focus:ring-indigo-500 block w-full rounded-md border-gray-300 p-1 shadow-sm sm:text-sm"
                        />
                        <button
                          v-if="
                            !isRegistrationInvalidDay(day.date) &&
                            day.status === 'adjusting' &&
                            (props.currentUser['is_admin_or_scheduler'] ===
                              true ||
                              props.currentUser['is_creator_or_agent'] === true)
                          "
                          type="button"
                          class="bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 inline-flex items-center rounded-full border border-transparent p-1 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
                          @click.prevent="toggleEditEntry"
                        >
                          <CheckCircleIcon class="h-5 w-5" aria-hidden="true" />
                        </button>
                      </div>
                    </template>
                    <template v-else>
                      <div class="flex items-center justify-between">
                        <div class="editable-text">
                          {{ day.entry_note }}
                        </div>
                        <button
                          v-if="
                            !isRegistrationInvalidDay(day.date) &&
                            day.status === 'adjusting' &&
                            (props.currentUser['is_admin_or_scheduler'] ===
                              true ||
                              props.currentUser['is_creator_or_agent'] === true)
                          "
                          type="button"
                          class="bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 inline-flex items-center rounded-full border border-transparent p-1 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
                          @click.prevent="toggleEditEntry(day.date)"
                        >
                          <PencilSquareIcon
                            class="h-5 w-5"
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                    </template>
                    <div
                      v-if="
                        v$.targetWorks.$each.$response.$errors[index]
                          .entry_note[0]
                      "
                      class="mt-2 flex text-sm font-semibold text-red-600"
                    >
                      <ExclamationCircleIcon
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                      />
                      <div>
                        {{
                          v$.targetWorks.$each.$response.$errors[index]
                            .entry_note[0].$message
                        }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- /End replace -->
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-button-lab.p-selectbutton) {
  .p-button.p-highlight {
    background: #ef8e58;
    border-color: #ef8e58;
  }

  .p-button:focus {
    box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #ef8e58,
      0 1px 2px 0 rgba(0, 0, 0, 0);
  }

  .p-button {
    padding: v-bind(labButtonPadding) 4%;
    width: calc(100% / 3);
  }
}

:deep(.p-button-creator.p-selectbutton) {
  .p-button.p-highlight {
    background: #e66a6a;
    border-color: #e66a6a;
  }

  .p-button:focus {
    box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #e66a6a,
      0 1px 2px 0 rgba(0, 0, 0, 0);
  }

  .p-button {
    padding: v-bind(creatorButtonPadding) 4%;
    width: calc(100% / 3);
  }
}
</style>
